import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { getAccountsTree, getAccountDetails, deleteAccount, toggleAccountStatus, clearError, clearSuccess, clearAccountDetails } from '../../../../redux/Slices/authSlice';
import AccountModal from '../../../components/AccountModal/AccountModal';
import DeleteConfirmModal from '../../../components/DeleteModal/DeleteConfirmModal';
import { toast } from 'react-toastify';
import './AccountsTree.css';
import { useTitle } from '../../../../shared/hooks/TitleContext';
import { useTranslation } from 'react-i18next';
import i18n from '../../../../i18n/i18n';

const JSTREE_CSS = 'https://cdnjs.cloudflare.com/ajax/libs/jstree/3.3.17/themes/default/style.min.css';
const JQUERY_JS = 'https://code.jquery.com/jquery-3.7.1.min.js';
const JSTREE_JS = 'https://cdnjs.cloudflare.com/ajax/libs/jstree/3.3.17/jstree.min.js';

function loadOnce(tagName, id, attributes = {}) {
  if (document.getElementById(id)) {
    if (tagName === 'script') {
      if (id === 'jquery-js' && (window.jQuery || window.$)) return Promise.resolve();
      if (id === 'jstree-js' && window.jQuery && window.jQuery.fn && window.jQuery.fn.jstree) return Promise.resolve();
      return new Promise((resolve) => {
        const checkInterval = setInterval(() => {
          if (id === 'jquery-js' && (window.jQuery || window.$)) {
            clearInterval(checkInterval);
            resolve();
          } else if (id === 'jstree-js' && window.jQuery && window.jQuery.fn && window.jQuery.fn.jstree) {
            clearInterval(checkInterval);
            resolve();
          }
        }, 50);
        setTimeout(() => {
          clearInterval(checkInterval);
          resolve();
        }, 5000);
      });
    }
    return Promise.resolve();
  }
  return new Promise((resolve, reject) => {
    const el = document.createElement(tagName);
    el.id = id;
    Object.entries(attributes).forEach(([k, v]) => el.setAttribute(k, v));
    if (tagName === 'script') {
      el.onload = () => {
        if (id === 'jquery-js') {
          if (window.jQuery || window.$) resolve();
          else setTimeout(() => resolve(), 100);
        } else if (id === 'jstree-js') {
          if (window.jQuery && window.jQuery.fn && window.jQuery.fn.jstree) resolve();
          else setTimeout(() => resolve(), 100);
        } else {
          resolve();
        }
      };
      el.onerror = reject;
    } else {
      el.onload = () => resolve();
      el.onerror = reject;
    }
    if (tagName === 'link') document.head.appendChild(el);
    else document.body.appendChild(el);
  });
}

const AccountsTree = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { setTitle } = useTitle();
  const { accountsTree, accountDetails, isLoading, error, success } = useSelector((state) => state.auth);
  const { t } = useTranslation('global');
  const isRTL = i18n.language === 'ar';
  const textAlign = isRTL ? 'right' : 'left';
  const dir = isRTL ? 'rtl' : 'ltr';
  
  useEffect(() => {
    setTitle('sidenav.accountsTree');
  }, [setTitle]);
  const [selectedNode, setSelectedNode] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState('add');
  const [pendingAction, setPendingAction] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, node: null });
  const [libsLoaded, setLibsLoaded] = useState(false);
  const lastErrorRef = useRef({ message: null, time: 0 });
  const openNodeIdsRef = useRef([]);
  const selectedNodeIdRef = useRef(null);
  const treeContainerRef = useRef(null);

  const getJsTreeInstance = () => {
    const $global = window.jQuery || window.$;
    if (!$global || !treeContainerRef.current) return null;
    if (!$global.fn || !$global.fn.jstree) return null;
    try {
      const inst = $global(treeContainerRef.current).jstree(true);
      return inst && typeof inst.get_selected === 'function' ? inst : null;
    } catch {
      return null;
    }
  };

  useEffect(() => {
    let isMounted = true;
    const init = async () => {
      try {
        await loadOnce('link', 'jstree-css', { rel: 'stylesheet', href: JSTREE_CSS });
        await loadOnce('script', 'jquery-js', { src: JQUERY_JS });
        let attempts = 0;
        while (!(window.jQuery || window.$) && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        await loadOnce('script', 'jstree-js', { src: JSTREE_JS });
        attempts = 0;
        while (!(window.jQuery && window.jQuery.fn && window.jQuery.fn.jstree) && attempts < 50) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        if (isMounted) setLibsLoaded(true);
      } catch (e) {
      }
    };
    init();
    return () => { isMounted = false; };
  }, []);

  useEffect(() => {
    dispatch(getAccountsTree());
    dispatch(clearAccountDetails());
    setSelectedNode(null);
  }, [dispatch, location.pathname]);

  useEffect(() => {
    return () => {
      dispatch(clearAccountDetails());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!error || !pendingAction) return;
    if (pendingAction === 'toggle' || pendingAction === 'delete') {
      const now = Date.now();
      const last = lastErrorRef.current;
      if (last.message !== error || now - last.time > 2000) {
        toast.error(error, { rtl: isRTL });
        lastErrorRef.current = { message: error, time: now };
      }
    }
    const timer = setTimeout(() => {
      dispatch(clearError());
      setPendingAction(null);
    }, 100);
    return () => clearTimeout(timer);
  }, [error, pendingAction, dispatch]);

  useEffect(() => {
    if (!success) return;
    if (pendingAction === 'toggle' || pendingAction === 'delete') {
      toast.success(success, { rtl: true });
    }
    if (pendingAction === 'add' || pendingAction === 'edit' || pendingAction === 'delete' || showModal) {
      const inst = getJsTreeInstance();
      if (inst) {
        try {
          openNodeIdsRef.current = inst.get_open_nodes ? inst.get_open_nodes() : [];
          const sel = inst.get_selected ? inst.get_selected() : [];
          selectedNodeIdRef.current = (sel && sel.length > 0 && pendingAction !== 'delete') ? sel[0] : null;
        } catch {}
      }
      // Reset account details for all actions (add, edit, delete)
      dispatch(clearAccountDetails());
      setSelectedNode(null);
      dispatch(getAccountsTree());
      setShowModal(false);
    } else if (pendingAction === 'toggle') {
      // Reset account details after toggle
      dispatch(clearAccountDetails());
      setSelectedNode(null);
    }
    setPendingAction(null);
    dispatch(clearSuccess());
  }, [success, pendingAction, showModal, accountDetails, dispatch]);

  const flattenTree = (nodes, allNodes = []) => {
    if (!nodes || !Array.isArray(nodes)) return allNodes;
    nodes.forEach(node => {
      let iconType = 'default';
      if (node.children && node.children.length > 0) {
        iconType = 'default';
      } else if (node.can_have_children) {
        iconType = 'default';
      } else {
        iconType = 'file';
      }
      const displayName = isRTL ? (node.name_ar || `العنصر ${node.id}`) : (node.name_en || `Item ${node.id}`);
      allNodes.push({
        id: String(node.id),
        parent: node.parent_id ? String(node.parent_id) : '#',
        text: displayName,
        type: iconType,
        data: node,
        li_attr: { 
          'data-can-have-children': node.can_have_children ? 'true' : 'false',
          'data-category': node.category || 'normal',
          'data-node-data': JSON.stringify(node)
        }
      });
      if (node.children && node.children.length > 0) {
        flattenTree(node.children, allNodes);
      }
    });
    return allNodes;
  };

  useEffect(() => {
    if (!libsLoaded) return;
    const $global = window.jQuery || window.$;
    if (!$global || !treeContainerRef.current) return;
    if (!accountsTree || accountsTree.length === 0) return;
    if (!$global.fn || !$global.fn.jstree) {
      return;
    }
    const $el = $global(treeContainerRef.current);
    try { $el.jstree('destroy'); } catch {}
    const jsTreeData = flattenTree(accountsTree);
    $el.jstree({
      core: {
        data: jsTreeData,
        multiple: false,
        animation: 0,
        check_callback: false,
        themes: {
          variant: 'default',
          stripes: true,
          dots: true,
          icons: true
        }
      },
      plugins: ['wholerow', 'types'],
      types: {
        default: { 
          icon: 'jstree-folder',
          valid_children: ['default', 'file']
        },
        file: { 
          icon: 'jstree-file',
          valid_children: []
        }
      }
    });
    $el.on('ready.jstree', function() {
      // Apply text direction to all tree nodes
      const treeContainer = $el.jstree(true).get_container();
      const direction = isRTL ? 'rtl' : 'ltr';
      const textAlign = isRTL ? 'right' : 'left';
      
      treeContainer.css('direction', direction);
      $global('.jstree-anchor', treeContainer).css({
        'text-align': textAlign,
        'direction': direction
      });
      $global('.jstree li', treeContainer).css('direction', direction);
      $global('.jstree-container-ul', treeContainer).css('direction', direction);
      
      // Reapply direction when tree updates
      $el.on('refresh.jstree', function() {
        treeContainer.css('direction', direction);
        $global('.jstree-anchor', treeContainer).css({
          'text-align': textAlign,
          'direction': direction
        });
        $global('.jstree li', treeContainer).css('direction', direction);
        $global('.jstree-container-ul', treeContainer).css('direction', direction);
      });
      
      treeContainer.delegate('a.jstree-anchor', 'click', function(e) {
        const jstree = $global.jstree.reference($el);
        const node = jstree.get_node(this);
        if ($global(e.target).closest('.jstree-ocl').length > 0) {
          return;
        }
        if (node.children && node.children.length > 0) {
          jstree.toggle_node(node);
        }
        jstree.select_node(node);
        e.preventDefault();
        return false;
      });
      setTimeout(() => {
        const inst = $el.jstree(true);
        const nodesToOpen = Array.isArray(openNodeIdsRef.current) ? openNodeIdsRef.current : [];
        const selectedNodeId = selectedNodeIdRef.current;
        if (nodesToOpen.length > 0) {
          const openNodesSequentially = (nodeIds, index = 0) => {
            if (index >= nodeIds.length) {
              if (selectedNodeId) {
                setTimeout(() => {
                  try {
                    const selectedData = inst.get_node(selectedNodeId);
                    if (selectedData && selectedData.data?.id) {
                      inst.select_node(selectedNodeId);
                      dispatch(getAccountDetails(selectedData.data.id));
                    }
                  } catch (e) {
                  }
                }, 100);
              }
              openNodeIdsRef.current = [];
              selectedNodeIdRef.current = null;
              return;
            }
            try {
              const nodeId = nodeIds[index];
              inst.open_node(nodeId, () => {
                openNodesSequentially(nodeIds, index + 1);
              });
            } catch (e) {
              openNodesSequentially(nodeIds, index + 1);
            }
          };
          openNodesSequentially(nodesToOpen);
        } else if (selectedNodeId) {
          try {
            inst.select_node(selectedNodeId);
          } catch {}
          openNodeIdsRef.current = [];
          selectedNodeIdRef.current = null;
        } else {
          openNodeIdsRef.current = [];
          selectedNodeIdRef.current = null;
        }
      }, 300);
    });
    $el.on('select_node.jstree', function (e, data) {
      let nodeData = null;
      const liAttr = data.node.li_attr || {};
      if (liAttr['data-node-data']) {
        nodeData = JSON.parse(liAttr['data-node-data']);
      } else if (data.node.original && data.node.original.data) {
        nodeData = data.node.original.data;
      } else if (data.node.data) {
        nodeData = data.node.data;
      }
      if (nodeData && nodeData.id) {
        setSelectedNode(nodeData);
        dispatch(getAccountDetails(nodeData.id));
      }
    });
    $el.on('contextmenu', '.jstree-anchor', function(e) {
      e.preventDefault();
      const jstree = $global.jstree.reference($el);
      const node = jstree.get_node(this);
      let nodeData = null;
      const liAttr = node.li_attr || {};
      if (liAttr['data-node-data']) {
        nodeData = JSON.parse(liAttr['data-node-data']);
      } else if (node.original && node.original.data) {
        nodeData = node.original.data;
      } else if (node.data) {
        nodeData = node.data;
      }
      if (nodeData && nodeData.id) {
        jstree.select_node(node);
        setContextMenu({
          visible: true,
          x: e.pageX,
          y: e.pageY,
          node: nodeData
        });
      }
    });
    $el.on('contextmenu', '.jstree-icon, .jstree-ocl', function(e) {
      e.preventDefault();
      const $targetAnchor = $global(e.currentTarget).closest('li').children('a.jstree-anchor');
      if ($targetAnchor && $targetAnchor.length) {
        $targetAnchor.trigger({ type: 'contextmenu', pageX: e.pageX, pageY: e.pageY, preventDefault: () => {} });
      }
    });
    return () => {
      try { $el.jstree('destroy'); } catch {}
    };
  }, [accountsTree, dispatch, libsLoaded, isRTL]);

  useEffect(() => {
    const handleClickOutside = () => {
      if (contextMenu.visible) {
        setContextMenu({ visible: false, x: 0, y: 0, node: null });
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [contextMenu.visible]);

  return (
    <div className="accounts-tree-container" style={{ direction: dir }}>
      <div className="tree-header">
        <h1 className="tree-title" style={{ textAlign }}>{t('accountsTree.title')}</h1>
      </div>
      <div className="tree-content">
        <div className="tree-view" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          {isLoading && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              position: 'absolute', inset: 0, zIndex: 2,
              background: 'rgba(0,0,0,0.15)'
            }}>
              <span style={{
                width: 28, height: 28, borderRadius: '50%', border: '3px solid #AC2000',
                borderTopColor: 'transparent', display: 'inline-block',
                animation: 'spin 1s linear infinite'
              }} />
            </div>
          )}
          <div id="accounts-tree" ref={treeContainerRef} style={{ direction: isRTL ? 'rtl' : 'ltr' }} />
        </div>
        <div className="tree-details">
          <div className="details-content">
            {accountDetails ? (
              <div>
                <div style={{ 
                  background: '#2d3748',
                  padding: '1rem',
                  borderRadius: '12px',
                  marginBottom: '1.5rem'
                  }}>
                    <h4 style={{ color: '#fff', marginBottom: '0.75rem', fontSize: '14px', fontWeight: 600, textAlign }}>{t('accountsTree.actions')}</h4>
                    <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
                      <button
                        onClick={() => {
                          setModalMode('add');
                          setShowModal(true);
                          setPendingAction('add');
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: '#AC2000',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 4px rgba(172, 32, 0, 0.2)'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#0db56a'}
                        onMouseLeave={(e) => e.target.style.background = '#AC2000'}
                      >
                        {t('accountsTree.add')}
                      </button>
                      <button
                        onClick={() => {
                          setModalMode('edit');
                          setShowModal(true);
                          setPendingAction('edit');
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: '#B3B3B3',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 4px rgba(179, 179, 179, 0.2)'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#999999'}
                        onMouseLeave={(e) => e.target.style.background = '#B3B3B3'}
                      >
                        {t('accountsTree.edit')}
                      </button>
                      <button
                        onClick={() => {
                          setShowDeleteConfirm(true);
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: '#F6630d',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          transition: 'all 0.2s',
                          boxShadow: '0 2px 4px rgba(246, 99, 13, 0.2)'
                        }}
                        onMouseEnter={(e) => e.target.style.background = '#d9540b'}
                        onMouseLeave={(e) => e.target.style.background = '#F6630d'}
                      >
                        {t('accountsTree.delete')}
                      </button>
                      <button
                        onClick={() => {
                          setPendingAction('toggle');
                          dispatch(toggleAccountStatus(accountDetails.id));
                        }}
                        style={{
                          padding: '0.5rem 0.75rem',
                          background: accountDetails.is_active ? '#F6630d' : '#AC2000',
                          border: 'none',
                          borderRadius: '6px',
                          color: '#fff',
                          cursor: 'pointer',
                          fontSize: '13px',
                          fontWeight: 600,
                          transition: 'all 0.2s',
                          boxShadow: `0 2px 4px ${accountDetails.is_active ? 'rgba(246, 99, 13, 0.2)' : 'rgba(172, 32, 0, 0.2)'}`
                        }}
                        onMouseEnter={(e) => e.target.style.background = accountDetails.is_active ? '#d9540b' : '#8a1a00'}
                        onMouseLeave={(e) => e.target.style.background = accountDetails.is_active ? '#F6630d' : '#AC2000'}
                      >
                        {accountDetails.is_active ? t('accountsTree.deactivate') : t('accountsTree.activate')}
                      </button>
                    </div>
                  </div>
                <h3 style={{ color: '#fff', marginBottom: '1rem', fontSize: '16px', textAlign }}>{t('accountsTree.accountDetails')}</h3>
                <div style={{ color: '#9ca3af', direction: dir }}>
                  <p style={{ textAlign, direction: isRTL ? 'rtl' : 'ltr' }}><strong>{t('accountsTree.name')}:</strong> {isRTL ? accountDetails.name_ar : accountDetails.name_en}</p>
                  <p style={{ textAlign }}><strong>{t('accountsTree.nameEn')}:</strong> {accountDetails.name_en || t('accountsTree.notAvailable')}</p>
                  <p style={{ textAlign }}><strong>{t('accountsTree.code')}:</strong> {accountDetails.code || t('accountsTree.notAvailable')}</p>
                  <p style={{ textAlign }}><strong>{t('accountsTree.type')}:</strong> {accountDetails.type || t('accountsTree.notSpecified')}</p>
                  <p style={{ textAlign }}><strong>{t('accountsTree.category')}:</strong> {accountDetails.category || t('accountsTree.notSpecified')}</p>
                  <p style={{ textAlign }}><strong>{t('accountsTree.isPostingAccount')}:</strong> {accountDetails.is_posting_account ? t('accountsTree.yes') : t('accountsTree.no')}</p>
                  <p style={{ textAlign }}><strong>{t('accountsTree.canHaveChildren')}:</strong> {accountDetails.can_have_children ? t('accountsTree.yes') : t('accountsTree.no')}</p>
                  <p style={{ textAlign }}><strong>{t('accountsTree.status')}:</strong> {accountDetails.is_active ? t('accountsTree.active') : t('accountsTree.inactive')}</p>
                  {accountDetails.parent && <p style={{ textAlign, direction: isRTL ? 'rtl' : 'ltr' }}><strong>{t('accountsTree.parentAccount')}:</strong> {isRTL ? accountDetails.parent.name_ar : accountDetails.parent.name_en}</p>}
                  {accountDetails.created_at && <p style={{ textAlign }}><strong>{t('accountsTree.createdAt')}:</strong> {new Date(accountDetails.created_at).toLocaleDateString('en-US')}</p>}
                </div>
              </div>
            ) : (
              <p style={{ color: '#6b7280', margin: 0, textAlign }}>
                {t('accountsTree.selectNode')}
              </p>
            )}
          </div>
        </div>
      </div>
      <AccountModal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        mode={modalMode}
        parentAccount={modalMode === 'add' ? accountDetails : null}
        accountData={modalMode === 'edit' ? accountDetails : null}
        onSuccess={() => {
          dispatch(clearAccountDetails());
          setSelectedNode(null);
          dispatch(getAccountsTree());
        }}
      />
      <DeleteConfirmModal
        isOpen={showDeleteConfirm}
        onClose={() => setShowDeleteConfirm(false)}
        onConfirm={() => {
          if (accountDetails?.id) {
            setPendingAction('delete');
            dispatch(deleteAccount(accountDetails.id));
            setShowDeleteConfirm(false);
          }
        }}
        accountName={isRTL ? accountDetails?.name_ar : accountDetails?.name_en}
        translationKeyPrefix="accountsTree"
      />
      {contextMenu.visible && contextMenu.node && (
        <div 
          className="context-menu accounts-tree-context-menu"
          style={{
            position: 'fixed',
            left: contextMenu.x,
            top: contextMenu.y,
            zIndex: 25000,
            backgroundColor: '#2d3748',
            border: '1px solid #4a5568',
            borderRadius: '8px',
            padding: '4px',
            minWidth: '180px',
            boxShadow: '0 10px 25px rgba(0, 0, 0, 0.3)',
            display: 'flex',
            flexDirection: 'column',
            gap: '2px',
            direction: dir
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <button 
            className="context-menu-item"
            style={{
              width: '100%',
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              color: 'white',
              textAlign,
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#4a5568'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
            onClick={() => {
              setModalMode('add');
              setShowModal(true);
              setPendingAction('add');
              setContextMenu({ visible: false, x: 0, y: 0, node: null });
              dispatch(getAccountDetails(contextMenu.node.id));
            }}
          >
            {t('accountsTree.add')}
          </button>
          <button 
            className="context-menu-item"
            style={{
              width: '100%',
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              color: 'white',
              textAlign,
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = '#4a5568'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
            onClick={() => {
              setModalMode('edit');
              setShowModal(true);
              setPendingAction('edit');
              setContextMenu({ visible: false, x: 0, y: 0, node: null });
              dispatch(getAccountDetails(contextMenu.node.id));
            }}
          >
            {t('accountsTree.edit')}
          </button>
          <button 
            className="context-menu-item danger"
            style={{
              width: '100%',
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              color: '#ef4444',
              textAlign,
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(239, 68, 68, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
            onClick={() => {
              setShowDeleteConfirm(true);
              setContextMenu({ visible: false, x: 0, y: 0, node: null });
              dispatch(getAccountDetails(contextMenu.node.id));
            }}
          >
            {t('accountsTree.delete')}
          </button>
          <button 
            className="context-menu-item warning"
            style={{
              width: '100%',
              padding: '10px 16px',
              background: 'transparent',
              border: 'none',
              color: '#f59e0b',
              textAlign,
              cursor: 'pointer',
              borderRadius: '4px',
              fontSize: '14px',
              transition: 'all 0.2s'
            }}
            onMouseEnter={(e) => e.target.style.background = 'rgba(245, 158, 11, 0.1)'}
            onMouseLeave={(e) => e.target.style.background = 'transparent'}
            onClick={() => {
              setPendingAction('toggle');
              dispatch(toggleAccountStatus(contextMenu.node.id));
              setContextMenu({ visible: false, x: 0, y: 0, node: null });
              dispatch(clearAccountDetails());
              setSelectedNode(null);
            }}
          >
            {contextMenu.node.is_active ? t('accountsTree.deactivate') : t('accountsTree.activate')}
          </button>
        </div>
      )}
    </div>
  );
};

export default AccountsTree;


